import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ScrollText } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { FadeUp, MaskedLines } from "@/components/Reveal";

const ARTICLES = [
  { num: "Art. 1", title: "Principiul fundamental", text: "Nimeni nu pleacă flămând. Cine pleacă flămând n-a citit meniul cu atenție." },
  { num: "Art. 2", title: "Dreptul la ciorbă", text: "Orice cetățean are dreptul la o ciorbă cinstită. Smântâna și ardeiul sunt drepturi suplimentare, nu obligații constituționale." },
  { num: "Art. 3", title: "Regimul juridic al micului", text: "Micul se servește fierbinte. Muștarul este recomandat. Pâinea este permisă. Berea poate constitui circumstanță favorizantă." },
  { num: "Art. 4", title: "Prezumția de nevinovăție", text: "Nimeni nu poate fi judecat pentru că a comandat încă patru mici după ce a declarat: „Eu iau doar ceva ușor.”" },
  { num: "Art. 5", title: "Protecția sarmalei", text: "Sarmaua este indivizibilă, inalienabilă și imprescriptibilă. Tentativa de a lua ultima sarma din farfuria altuia fără consimțământ poate atrage represalii imediate." },
  { num: "Art. 6", title: "Mămăliga", text: "Mămăliga nu este garnitură. Este instituție." },
  { num: "Art. 7", title: "Supremația grătarului", text: "Carnea de pe grătar trebuie să sfârâie. Dacă masa de alături întoarce capul când vine farfuria, prepararea se consideră reușită." },
  { num: "Art. 8", title: "Dreptul la pâine", text: "Este garantat dreptul de a înmuia pâinea în sos. Farfuria prea curată nu constituie abatere de la bunele maniere, ci compliment adresat bucătarului." },
  { num: "Art. 9", title: "Regimul pizzei", text: "Italienii au inventat pizza. Îi respectăm pentru asta. Cuptorul vine din Italia. Ospățul e românesc." },
  { num: "Art. 10", title: "Ultima felie", text: "Ultima felie de pizza aparține celui care are curajul s-o ia. Formula „O mai vrea cineva?” trebuie rostită o singură dată și cu o pauză rezonabilă de maximum trei secunde." },
  { num: "Art. 11", title: "Infracțiunea de ciugulire", text: "Persoana care declară „Eu nu comand nimic, gust doar de la tine” poate fi obligată prin hotărârea unanimă a mesei să-și comande propria porție." },
  { num: "Art. 12", title: "Cartofii altuia", text: "Cartofii din farfuria altuia sunt proprietate privată. Furtul unuia este tolerat. De la al treilea încolo vorbim despre confiscare." },
  { num: "Art. 13", title: "Egalitatea în fața desertului", text: "Toți oamenii sunt egali în fața papanașilor. Cei care spun „eu nu mănânc dulce” își pierd temporar dreptul de a cere „doar o linguriță”." },
  { num: "Art. 14", title: "Papanașii „la comun”", text: "Noțiunea de „luăm niște papanași la comun” este lipsită de efecte juridice. Experiența demonstrează că, în final, cineva mănâncă mai mult." },
  { num: "Art. 15", title: "Dieta", text: "Dieta este respectată pe întreg teritoriul restaurantului. Intrarea în vigoare a acesteia poate fi însă amânată până luni." },
  { num: "Art. 16", title: "Salata", text: "Salata este sănătoasă. Mai ales lângă patru mici." },
  { num: "Art. 17", title: "Dreptul la răzgândire", text: "Orice persoană care spune „Nu mai pot!” are dreptul constituțional să se răzgândească atunci când vede desertul." },
  { num: "Art. 18", title: "Libertatea de exprimare", text: "Sunt garantate expresiile: „Mamă, ce bun e!”, „Mai adu unul!”, „Dă-mi și mie să gust!” și „De mâine țin regim.”" },
  { num: "Art. 19", title: "Obligația de bună dispoziție", text: "Masa se ia fără grabă. Problemele rămân afară. Telefonul poate sta și el cinci minute liniștit. Omul de lângă tine merită măcar cât Instagramul." },
  { num: "Art. 20", title: "Cetățenia de onoare", text: "La Nea Mitică vii prima dată client. Dacă revii, ești de-al casei." },
];

const Constitution = () => (
  <div className="min-h-screen bg-[#121110]">
    <Navbar />
    <div className="grain relative mx-auto max-w-3xl px-5 pb-24 pt-32 sm:px-8">
      <Link
        to="/"
        data-testid="constitution-back-link"
        className="mb-10 inline-flex items-center gap-2 text-sm font-semibold text-stone-400 transition-colors hover:text-red-400"
      >
        <ArrowLeft className="h-4 w-4" /> Înapoi la casele lui Mitică
      </Link>

      <motion.div
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
        className="relative rounded-3xl bg-[#FAF6F0] px-6 py-12 text-stone-800 shadow-2xl sm:px-14 sm:py-16"
      >
        <div className="absolute -top-10 left-1/2 -translate-x-1/2">
          <img
            src="/assets/logo-zoo.jpg"
            alt="Sigiliul lui Nea Mitică"
            className="h-20 w-20 rounded-full object-cover shadow-xl ring-4 ring-red-600"
          />
        </div>

        <div className="mt-8 text-center">
          <p className="text-xs font-bold uppercase tracking-[0.35em] text-red-600">Republica Pofticioșilor</p>
          <MaskedLines
            lines={["CONSTITUȚIA", "LUI NEA MITICĂ"]}
            delay={0.3}
            className="mt-4"
            lineClassName="font-display text-4xl font-black leading-tight tracking-tight text-[#1C1A17] sm:text-5xl"
          />
          <p className="font-hand mt-4 text-2xl text-red-600">Legea fundamentală a omului pofticios</p>
        </div>

        <FadeUp delay={0.5} className="mt-10">
          <h2 className="font-display text-center text-lg font-bold uppercase tracking-widest">Preambul</h2>
          <p className="mx-auto mt-4 max-w-xl text-center text-base italic leading-relaxed text-stone-600">
            Noi, oamenii care credem că masa nu-i doar despre mâncare, ci despre poftă, prieteni, povești și încă o porție, adoptăm prezenta Constituție. Se aplică tuturor celor care intră la Nea Mitică, indiferent de vârstă, domiciliu, grad de foame sau numărul de calorii promis solemn că va fi respectat.
          </p>
        </FadeUp>

        <div className="mt-12 space-y-8">
          {ARTICLES.map((a, i) => (
            <FadeUp key={a.num} delay={Math.min(i * 0.03, 0.3)}>
              <article data-testid={`constitution-${a.num.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} className="border-l-2 border-red-600/30 pl-5">
                <h3 className="font-display text-base font-bold text-[#1C1A17]">
                  <span className="mr-2 text-red-600">{a.num}</span> — {a.title}
                </h3>
                <p className="mt-1.5 text-base leading-relaxed text-stone-600">{a.text}</p>
              </article>
            </FadeUp>
          ))}
        </div>

        <FadeUp className="mt-14 border-t-2 border-dashed border-red-600/30 pt-8">
          <h2 className="font-display text-center text-lg font-bold uppercase tracking-widest text-red-600">Dispoziție finală</h2>
          <p className="mx-auto mt-4 max-w-xl text-center text-base italic leading-relaxed text-stone-600">
            Prezenta Constituție intră în vigoare imediat ce ți s-a făcut poftă. Orice lege, dietă, promisiune făcută nutriționistului sau declarație de tipul „eu doar beau ceva” care contravine prezentei Constituții se suspendă pe durata mesei.
          </p>
          <p className="mt-6 text-center text-sm text-stone-500">
            Dată la București, sub autoritatea supremă a bunului gust.
          </p>
          <div className="mt-8 text-center">
            <img src="/assets/zoo/mitica-gratar.jpg" alt="Nea Mitică" className="mx-auto h-16 w-16 rounded-full object-cover ring-2 ring-red-600" />
            <p className="font-display mt-3 text-xl font-black tracking-wide text-[#1C1A17]">NEA MITICĂ</p>
            <p className="text-sm text-stone-500">Președinte pe viață al mesei bine puse</p>
            <p className="font-hand mt-3 text-2xl text-red-600">„Pofta poporului este suverană.”</p>
          </div>
        </FadeUp>
      </motion.div>

      <div className="mt-10 text-center">
        <Link
          to="/"
          data-testid="constitution-home-cta"
          className="inline-flex items-center gap-2 rounded-full bg-red-600 px-7 py-3.5 text-sm font-bold text-white transition-transform hover:scale-[1.03]"
        >
          <ScrollText className="h-4 w-4" /> Citește și meniul — e tot un fel de lege
        </Link>
      </div>
    </div>
    <Footer />
  </div>
);

export default Constitution;
